import React, { useState, useEffect, useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, LabelList, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import {
    Dashboard as LayoutDashboard,
    FactCheck, AssignmentLate, Category, ThumbUp, ThumbDown, Star,
    Paid, LocalShipping, Group
} from '@mui/icons-material';
import { AvaliacoesDashboardData, fetchAvaliacoesDashboard } from '../sample-analysis/services/avaliacoesAPI';
import styles from './DashboardAmostrasPage.module.css';

// ==========================================
// CORE THEME SETTINGS (Matched 100% to Producao)
// ==========================================
const RED = '#BA0001';
const NAVY = '#1B2A49';
const CHART_COLORS = ['#BA0001', '#1B2A49', '#475569', '#64748b', '#94a3b8'];

// ==========================================
// CUSTOM COMPONENTS
// ==========================================
const KpiCard = ({ title, mainValue, subLabel1, subLabel2, icon: Icon, accent = false }: any) => (
    <div className={styles.kpiCard}>
        <div className={styles.kpiHeader}>
            <h3 className={styles.kpiTitle}>{title}</h3>
            <div className={`${styles.kpiIconWrapper} ${accent ? styles.kpiIconAccent : ''}`}>
                <Icon sx={{ fontSize: 16 }} />
            </div>
        </div>
        <div className={styles.kpiContent}>
            <div className={styles.kpiValue}>{mainValue}</div>
            <div className={styles.kpiSubLabel}>
                <span>{subLabel1}</span>
                {subLabel2 && <div>{subLabel2}</div>}
            </div>
        </div>
    </div>
);

const ChartWrapper = ({ title, children }: any) => (
    <div className={styles.chartWrapper}>
        <h3 className={styles.chartWrapperTitle}>{title}</h3>
        <div className={styles.chartContainer}>
            {children}
        </div>
    </div>
);

const formatCurrency = (value: any) => {
    const val = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(val)) return 'R$ 0,00';
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const data = payload[0].payload;
    const qtd = data.quantidade;

    return (
        <div className={styles.customTooltip}>
            <p className={styles.customTooltipTitle}>{label || data.name || data.vendedor || data.referencia || data.segmento || data.subject}</p>
            {qtd !== undefined && (
                <div className={styles.tooltipRow}>
                    <span className={styles.tooltipLabel}>Avaliações</span>
                    <span className={styles.tooltipValue}>{qtd}</span>
                </div>
            )}
            {data.notaMedia !== undefined && data.notaMedia !== null && (
                <div className={styles.tooltipRow}>
                    <span className={styles.tooltipLabel}>Nota Média</span>
                    <span className={styles.tooltipValue}>{Number(data.notaMedia).toFixed(1)} ★</span>
                </div>
            )}
            {data.taxaAprovacao !== undefined && (
                <div className={styles.tooltipRow}>
                    <span className={styles.tooltipLabel}>Taxa Aprovação</span>
                    <span className={styles.tooltipValue}>{data.taxaAprovacao}%</span>
                </div>
            )}
            {data.valor !== undefined && data.valor !== null && (
                 <div className={styles.tooltipRow}>
                     <span className={styles.tooltipLabel}>Valor</span>
                     <span className={styles.tooltipValue}>{formatCurrency(data.valor)}</span>
                 </div>
            )}
            {data.value !== undefined && data.value !== null && data.subject !== undefined && (
                 <div className={styles.tooltipRow}>
                 <span className={styles.tooltipLabel}>Nota</span>
                 <span className={styles.tooltipValue}>{Number(data.value).toFixed(1)} ★</span>
             </div>
            )}
        </div>
    );
};

// ==========================================
// DONUT CHART CUSTOM LABEL (Premium Style)
// ==========================================
const DonutLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, payload, fill }: any) => {
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = (Number(cx) || 0) + (outerRadius + 0) * cos;
    const sy = (Number(cy) || 0) + (outerRadius + 0) * sin;
    const mx = (Number(cx) || 0) + (outerRadius + 20) * cos;
    const my = (Number(cy) || 0) + (outerRadius + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    const name = payload?.segmento || 'N/A';
    const subText = payload?.taxaAprovacao !== undefined
        ? `${payload.taxaAprovacao}% Aprovação`
        : `${((percent || 0) * 100).toFixed(0)}% | ${payload?.quantidade || 0} envios`;

    return (
        <g>
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" strokeWidth={1.5} />
            <circle cx={ex} cy={ey} r={2.5} fill={fill} />
            <text x={ex + (cos >= 0 ? 8 : -8)} y={ey} textAnchor={textAnchor} dominantBaseline="central">
                <tspan x={ex + (cos >= 0 ? 8 : -8)} dy="-0.6em" fontSize="11" fontWeight="800" fill="#1e293b" style={{ textTransform: 'uppercase' }}>{name}</tspan>
                <tspan x={ex + (cos >= 0 ? 8 : -8)} dy="1.4em" fontSize="10" fill="#64748b" fontWeight="600">{subText}</tspan>
            </text>
        </g>
    );
};

// ==========================================
// SINGLE-LINE ROBUST LABELS
// ==========================================
const HorizontalLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    if (value === undefined || value === null) return null;
    
    return (
        <text
            x={(Number(x) || 0) + (Number(width) || 0) + 8}
            y={(Number(y) || 0) + (Number(height) || 0) / 2}
            fill="#1e293b"
            fontSize={10}
            fontWeight={900}
            dominantBaseline="central"
            style={{ pointerEvents: 'none' }}
        >
            {value}
        </text>
    );
};

const VerticalLabel = (props: any) => {
    const { x, y, width, value } = props;
    if (value === undefined || value === null) return null;
    return (
        <text
            x={(Number(x) || 0) + (Number(width) || 0) / 2}
            y={(Number(y) || 0) - 10}
            fill="#1e293b"
            textAnchor="middle"
            fontSize={10}
            fontWeight={900}
            style={{ pointerEvents: 'none' }}
        >
            {value}
        </text>
    );
};

// ==========================================
// MAIN DASHBOARD COMPONENT
// ==========================================
const DashboardAmostrasPage: React.FC = () => {
    const [periodo, setPeriodo] = useState('mensal');
    const [dataI, setDataI] = useState<string>('2025-01-01');
    const [dataF, setDataF] = useState<string>('2026-12-31');

    const [dashboardData, setDashboardData] = useState<AvaliacoesDashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await fetchAvaliacoesDashboard(dataI, dataF);
                setDashboardData(result.data);
            } catch (err: any) {
                setError(err.message || 'Erro ao carregar dados do dashboard.');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [dataI, dataF]);


    const enrichedData = useMemo(() => {
        if (!dashboardData) return null;
        const TOP_N_V = 8;

        const reps = (dashboardData.topReps || []).slice(0, TOP_N_V);
        const enrichedReps = reps.map(r => ({
            ...r,
            richLabel: `${formatCurrency(r.valor)} | ${r.quantidade} env. | ${r.taxaAprovacao || 0}% apr.`
        }));

        const refs = (dashboardData.topReferencias || []).slice(0, TOP_N_V);
        const enrichedRefs = refs.map(r => ({
            ...r,
            richLabel: `${formatCurrency(r.valor)} | ${r.quantidade} aval.`
        }));

        const radarData = [
            { subject: 'Conforto', value: Number(dashboardData.criterios?.mediaConforto || 0), fullMark: 5 },
            { subject: 'Durabilidade', value: Number(dashboardData.criterios?.mediaDurabilidade || 0), fullMark: 5 },
            { subject: 'Proteção', value: Number(dashboardData.criterios?.mediaProtecao || 0), fullMark: 5 },
            { subject: 'Custo-Benefício', value: Number(dashboardData.criterios?.mediaCustoBeneficio || 0), fullMark: 5 }
        ];

        const enrichedClientes = (dashboardData.topClientes || []).slice(0, TOP_N_V).map(c => ({
            ...c,
            richLabel: `${formatCurrency(c.valor)} | ${c.quantidade} pedidos`
        }));

        return { enrichedReps, enrichedRefs, radarData, enrichedClientes };
    }, [dashboardData]);

    if (loading) return <div className={styles.stateContainer}>Carregando Dashboard...</div>;
    if (error) return <div className={`${styles.stateContainer} ${styles.stateError}`}>Erro: {error}</div>;
    if (!dashboardData || !enrichedData) return null;

    const { kpis, topSegmentos, topClientes } = dashboardData;
    const { enrichedReps, enrichedRefs, radarData, enrichedClientes } = enrichedData;
    const TOP_N = 8;

    return (
        <div className={styles.container}>
            {/* Header */}
            <header className={styles.header}>
                <div className={styles.filterGroup}>
                    <div className={styles.periodButtons}>
                        {['mensal', 'semanal', 'custom'].map(p => (
                            <button key={p} onClick={() => setPeriodo(p)} className={`${styles.periodBtn} ${periodo === p ? styles.periodBtnActive : ''}`}>
                                {p === 'custom' ? 'Personalizado' : p}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            {/* KPIs */}
            <div className={styles.kpiGrid}>
                <KpiCard title="Amostras Enviadas" mainValue={kpis?.totalQuantidadeAmostras || 0} subLabel1="total no período" icon={LocalShipping} accent />
                <KpiCard title="Valor em Amostras" mainValue={formatCurrency(kpis?.totalValorAmostras || 0)} subLabel1="investimento total" icon={Paid} />
                <KpiCard title="Taxa de Aprovação" mainValue={`${kpis?.taxaAprovacao || 0}%`} subLabel1="global" icon={Star} />
                <KpiCard title="Aprovadas" mainValue={kpis?.totalAprovados || 0} subLabel1="atendem à necessidade" icon={ThumbUp} />
                <KpiCard title="Reprovadas" mainValue={kpis?.totalReprovados || 0} subLabel1="não atendem" icon={ThumbDown} />
            </div>

            {/* Charts Grid */}
            <div className={styles.chartsGrid}>

                {/* Radar Chart (Critérios) */}
                <ChartWrapper title="Média de Avaliação dos Critérios (1-5)">
                    <ResponsiveContainer width="100%" height="100%">
                        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                            <PolarGrid stroke="#e2e8f0" />
                            <PolarAngleAxis dataKey="subject" tick={{ fill: '#475569', fontSize: 11, fontWeight: 600 }} />
                            <PolarRadiusAxis angle={30} domain={[0, 5]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                            <Radar name="Média" dataKey="value" stroke={NAVY} fill={NAVY} fillOpacity={0.5} />
                            <Tooltip content={<CustomTooltip />} />
                        </RadarChart>
                    </ResponsiveContainer>
                </ChartWrapper>

                {/* Reps */}
                <ChartWrapper title={`Avaliação de Consultores`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={enrichedReps || []} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                            <XAxis type="number" hide domain={[0, (dataMax: number) => dataMax * 1.5]} />
                            <YAxis dataKey="vendedor" type="category" axisLine={false} tickLine={false} tick={{ fill: '#1e293b', fontSize: 10, fontWeight: 700 }} width={100} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="taxaAprovacao" fill={NAVY} radius={[0, 4, 4, 0]} barSize={16}>
                                <LabelList dataKey="richLabel" content={<HorizontalLabel />} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartWrapper>

                {/* Segments */}
                <ChartWrapper title={`Taxa de Aprovação por Segmento`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 0, right: 60, left: 60, bottom: 0 }}>
                            <Pie data={(topSegmentos || []).slice(0, TOP_N)} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={2} dataKey="quantidade" nameKey="segmento" stroke="none" labelLine={false} label={<DonutLabel />}>
                                {(topSegmentos || []).slice(0, TOP_N).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartWrapper>

                {/* References */}
                <ChartWrapper title={`Top ${TOP_N} · Referências Avaliadas`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={enrichedRefs || []} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                            <XAxis type="number" hide domain={[0, (dataMax: number) => dataMax * 1.5]} />
                            <YAxis dataKey="referencia" type="category" axisLine={false} tickLine={false} tick={{ fill: '#1e293b', fontSize: 10, fontWeight: 700 }} width={100} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="quantidade" fill={RED} radius={[0, 4, 4, 0]} barSize={16}>
                                <LabelList dataKey="richLabel" content={<HorizontalLabel />} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartWrapper>

                {/* Ranking de Clientes */}
                <ChartWrapper title={`Top ${TOP_N} · Clientes que mais solicitam`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={enrichedClientes || []} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                            <XAxis type="number" hide domain={[0, (dataMax: number) => dataMax * 1.5]} />
                            <YAxis dataKey="cliente" type="category" axisLine={false} tickLine={false} tick={{ fill: '#1e293b', fontSize: 10, fontWeight: 700 }} width={100} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="quantidade" fill={NAVY} radius={[0, 4, 4, 0]} barSize={16}>
                                <LabelList dataKey="richLabel" content={<HorizontalLabel />} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartWrapper>

            </div>
        </div>
    );
};

export default DashboardAmostrasPage;
